/* eslint-disable max-lines */
import { Role, Team, User } from '@litlingo/client';
import {
  addUsers,
  selectUsers,
  setImportErrors,
  setIsImporting,
} from 'actions/userPermissionsPage';
import fileExtensionIconParser from 'constants/fileIcons';
import useAllTeams from 'hooks/teams/useAllTeams';
import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedUsers } from 'selectors/userPermissionsPage';
import { loadAndParseXLSXToJSON, parseUsersFromXLSXToJSON } from 'utils/files';
import { v4 } from 'uuid';

const AddUserCard: React.FC = () => {
  const dispatch = useDispatch();

  const selectedUsers = useSelector(getSelectedUsers);

  const { data: teams } = useAllTeams();
  const { register, getValues, setValue, handleSubmit } = useForm<User>();

  const inputRef = useRef<HTMLInputElement>();

  const onSubmit = (): void => {
    const { name, email } = getValues();

    const user = {
      name,
      email,
      uuid: v4(),
      roles: [] as Role[],
      sync_emails: false,
      teams: [] as Team[],
      privacy_level: 'standard',
    } as User;

    dispatch(addUsers([user]));

    setValue('name', '');
    setValue('email', '');
    inputRef.current?.focus();
  };

  const loadFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files) return;

    loadAndParseXLSXToJSON(e.target.files[0], (sheets) => {
      const { users, errors } = parseUsersFromXLSXToJSON(sheets, teams?.records || []);

      dispatch(addUsers(users));
      dispatch(setIsImporting(true));
      dispatch(setImportErrors(errors));
      dispatch(selectUsers(users));
    });
  };

  const renderAddUser = (
    <div
      className="w-full py-6 pl-6 pr-11 flex flex-col gap-2 bg-white rounded-lg"
      style={{ filter: 'drop-shadow(5px 6px 10px rgba(0, 0, 0, 0.10))' }}
    >
      <span className="text-heading-2">Add Users</span>
      <div className="w-full flex flex-row">
        <div className="w-1/2 py-4 pr-18 flex flex-col gap-3 overflow-hidden">
          <span className="text-body">
            Download the starter file, fill in the rows and import your users.
          </span>
          <div className="flex flex-col gap-2.5">
            <div className="flex flex-row items-center gap-2">
              <span className="w-5 h-5">{fileExtensionIconParser('xlsx')}</span>
              <a
                className="text-small underline cursor-pointer"
                href="https://lit-public-static-content.s3.us-east-2.amazonaws.com/user-spreadsheet.csv"
                target="_blank"
                rel="noopener noreferrer"
              >
                UserListStarterFile.xlsx
              </a>
            </div>
            <label
              htmlFor="file-input"
              className={`self-end h-8 inline-flex items-center px-4 py-2 text-body rounded bg-white border border-litlingo-gray-3 ${
                selectedUsers.length > 0 ? 'opacity-75 cursor-default' : 'cursor-pointer'
              }`}
            >
              <span className="text-litlingo-gray-5 font-bold">Import Users</span>
              <input
                id="file-input"
                type="file"
                className="hidden"
                onChange={loadFile}
                disabled={selectedUsers.length > 0}
              />
            </label>
          </div>
        </div>
        <div className="border-r border-litlingo-gray-1" />
        <div className="w-1/2 pl-18 flex flex-col gap-2.5">
          <span className="font-bold">OR add users manually</span>
          <div className="flex flex-col gap-2 pr-20">
            <div className="flex flex-col gap-1">
              <label htmlFor="name" className="text-body">
                Name <span className="text-litlingo-alert">*</span>
                <div className="rounded-md">
                  <input
                    id="name"
                    name="name"
                    ref={(e: HTMLInputElement): void => {
                      register(e, { required: true });
                      inputRef.current = e;
                    }}
                    placeholder="Name"
                    className="h-9 form-input w-full transition duration-150 ease-in-out placeholder-italic"
                    disabled={selectedUsers.length > 0}
                  />
                </div>
              </label>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="email" className="text-body">
                Email <span className="text-litlingo-alert">*</span>
                <div className="rounded-md">
                  <input
                    id="email"
                    name="email"
                    ref={register({ required: true })}
                    placeholder="Email"
                    className="h-9 form-input w-full transition duration-150 ease-in-out placeholder-italic"
                    disabled={selectedUsers.length > 0}
                  />
                </div>
              </label>
            </div>
          </div>
          <div className="flex flex-row gap-2.5 self-end">
            <button
              type="button"
              className="w-30 mt-3.5 flex justify-center button button--secondary h-8"
              onClick={handleSubmit(onSubmit)}
              disabled={selectedUsers.length > 0}
            >
              <span className="font-bold">Save & Next</span>
            </button>
            <button
              type="button"
              className="w-30 mt-3.5 flex justify-center button button--primary h-8"
              onClick={handleSubmit(onSubmit)}
              disabled={selectedUsers.length > 0}
            >
              <span className="font-bold">Save</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return renderAddUser;
};

export default AddUserCard;
